/**
 * InterviewDebriefView
 *
 * Renders the post-interview retrospective: score, strengths,
 * improvements, and learning resources.
 */

import { useLanguage } from '../../contexts/LanguageContext';
import { InterviewDebrief, InterviewSession } from '../../types/interviewPractice';
import './InterviewDebriefView.css';

interface Props {
  debrief: InterviewDebrief;
  session: InterviewSession | null;
}

export function InterviewDebriefView({ debrief, session }: Props) {
  const { t } = useLanguage();

  const scoreColor =
    debrief.overallScore >= 75 ? '#16a34a' :
    debrief.overallScore >= 50 ? '#d97706' : '#dc2626';

  return (
    <div className="debrief-container">
      <h1 className="debrief-title">{t('interviewPractice.debrief.title')}</h1>

      {session && (
        <p className="debrief-meta">
          {session.jobPosition.title} @ {session.jobPosition.company} &mdash;{' '}
          {session.durationMinutes} min &bull; {session.questions.length} questions
        </p>
      )}

      {/* Overall score */}
      <div className="debrief-score-card">
        <div className="debrief-score-circle" style={{ borderColor: scoreColor }}>
          <span className="debrief-score-value" style={{ color: scoreColor }}>
            {debrief.overallScore}
          </span>
          <span className="debrief-score-label">/100</span>
        </div>
        <p className="debrief-summary">{debrief.summary}</p>
      </div>

      {/* Strengths */}
      {debrief.strengths.length > 0 && (
        <section className="debrief-section debrief-strengths">
          <h2>
            <span className="debrief-icon">&#10003;</span>{' '}
            {t('interviewPractice.debrief.strengths')}
          </h2>
          <ul>
            {debrief.strengths.map((s, i) => (
              <li key={i}>
                <strong>{s.summary}</strong>
                {s.questionIds.length > 0 && (
                  <span className="debrief-qids">
                    ({s.questionIds.map((qid) => {
                      const idx = session?.questions.findIndex((q) => q.id === qid);
                      return idx !== undefined && idx >= 0 ? `Q${idx + 1}` : qid;
                    }).join(', ')})
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Areas for improvement */}
      {debrief.improvements.length > 0 && (
        <section className="debrief-section debrief-improvements">
          <h2>
            <span className="debrief-icon">&#9998;</span>{' '}
            {t('interviewPractice.debrief.improvements')}
          </h2>
          <ul>
            {debrief.improvements.map((imp, i) => (
              <li key={i}>
                <strong>{imp.summary}</strong>
                <p className="debrief-tip">{imp.tip}</p>
                {imp.questionIds.length > 0 && (
                  <span className="debrief-qids">
                    ({imp.questionIds.map((qid) => {
                      const idx = session?.questions.findIndex((q) => q.id === qid);
                      return idx !== undefined && idx >= 0 ? `Q${idx + 1}` : qid;
                    }).join(', ')})
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Learning resources */}
      {debrief.resources.length > 0 && (
        <section className="debrief-section debrief-resources">
          <h2>
            <span className="debrief-icon">&#128218;</span>{' '}
            {t('interviewPractice.debrief.resources')}
          </h2>
          <div className="debrief-resource-grid">
            {debrief.resources.map((r, i) => (
              <div className="debrief-resource-card" key={i}>
                <span className={`debrief-resource-type debrief-rt-${r.type}`}>{r.type}</span>
                <h3>{r.url ? <a href={r.url} target="_blank" rel="noreferrer">{r.title}</a> : r.title}</h3>
                <p>{r.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
